import {Repository} from '@s-ui/domain'

export class LocalStorageAssistantRepository extends Repository {
  #localStorage
  #keys
  #chunkSize
  #historyTtlMs
  #schemaVersion

  constructor({localStorage, config}) {
    super()
    const {CHAT_STORAGE_KEYS, CHAT_STORAGE_CHUNK_SIZE, CHAT_HISTORY_TTL_MS, CHAT_STORAGE_SCHEMA_VERSION} =
      config.get('COMMON').AI_ASSISTANT

    this.#localStorage = localStorage
    this.#keys = CHAT_STORAGE_KEYS
    this.#chunkSize = CHAT_STORAGE_CHUNK_SIZE
    this.#historyTtlMs = CHAT_HISTORY_TTL_MS
    this.#schemaVersion = CHAT_STORAGE_SCHEMA_VERSION
  }

  static create({config}) {
    return new LocalStorageAssistantRepository({localStorage: window.localStorage, config})
  }

  saveActiveChat({chatId, content}) {
    try {
      const serialized = JSON.stringify({chatId, content})
      const chunks = []
      for (let offset = 0; offset < serialized.length; offset += this.#chunkSize) {
        chunks.push(serialized.slice(offset, offset + this.#chunkSize))
      }

      // Extract title from first user message
      const title = content?.find(msg => msg.role === 'user')?.text || ''
      const savedAt = Date.now()

      // Check if last message is from user (waiting for AI response)
      const loading = content?.length > 0 && content[content.length - 1]?.role === 'user'

      // Write chunks first, then meta, then update chats list, then index — so a partial write leaves no dangling index pointer
      chunks.forEach((chunk, i) => this.#localStorage.setItem(this.#keys.chunk(chatId, i), chunk))

      this.#localStorage.setItem(
        this.#keys.chatMeta(chatId),
        JSON.stringify({id: chatId, title, savedAt, totalChunks: chunks.length})
      )

      // Update the chats list
      this.#updateChatsList({chatId, title, savedAt, loading})

      this.#localStorage.setItem(this.#keys.INDEX, JSON.stringify({activeId: chatId, version: this.#schemaVersion}))
    } catch {
      // Storage full or unavailable — skip silently
    }
  }

  #updateChatsList({chatId, title, savedAt, loading = false}) {
    try {
      const chatsListRaw = this.#localStorage.getItem(this.#keys.CHATS_LIST)
      const chatsList = chatsListRaw ? JSON.parse(chatsListRaw) : []

      // Remove existing entry for this chatId if present
      const filteredList = chatsList.filter(chat => chat.id !== chatId)

      // Add updated entry
      filteredList.push({id: chatId, title, savedAt, loading})

      this.#localStorage.setItem(this.#keys.CHATS_LIST, JSON.stringify(filteredList))
    } catch {
      // ignore
    }
  }

  #removeChatFromList(chatId) {
    try {
      const chatsListRaw = this.#localStorage.getItem(this.#keys.CHATS_LIST)
      if (!chatsListRaw) return

      const chatsList = JSON.parse(chatsListRaw)
      const filteredList = chatsList.filter(chat => chat.id !== chatId)

      this.#localStorage.setItem(this.#keys.CHATS_LIST, JSON.stringify(filteredList))
    } catch {
      // ignore
    }
  }

  #purgeOrphanedChunks() {
    try {
      const orphanIds = new Set()

      for (let i = 0; i < this.#localStorage.length; i++) {
        const key = this.#localStorage.key(i)
        if (!key?.startsWith(this.#keys.CHUNK_PREFIX)) continue

        // key shape: net_chat:chunk:{chatId}:{chunkIndex}
        const withoutPrefix = key.slice(this.#keys.CHUNK_PREFIX.length)
        const lastColon = withoutPrefix.lastIndexOf(':')
        if (lastColon === -1) continue

        const chatId = withoutPrefix.slice(0, lastColon)
        if (!this.#localStorage.getItem(this.#keys.chatMeta(chatId))) orphanIds.add(chatId)
      }

      orphanIds.forEach(chatId => {
        // No meta means we cannot know totalChunks — scan forward until we find no key
        for (let i = 0; ; i++) {
          const key = this.#keys.chunk(chatId, i)
          if (this.#localStorage.getItem(key) === null) break
          this.#localStorage.removeItem(key)
        }
      })
    } catch {
      // ignore
    }
  }

  loadChat(chatId) {
    try {
      const metaRaw = this.#localStorage.getItem(this.#keys.chatMeta(chatId))
      if (!metaRaw) return null

      const meta = JSON.parse(metaRaw)

      if (Date.now() - meta.savedAt > this.#historyTtlMs) {
        this.#clearChat(chatId)
        this.#removeChatFromList(chatId)
        return null
      }

      const parts = []
      for (let i = 0; i < meta.totalChunks; i++) {
        const part = this.#localStorage.getItem(this.#keys.chunk(chatId, i))
        if (part === null) {
          this.#clearChat(chatId)
          this.#removeChatFromList(chatId)
          return null
        }
        parts.push(part)
      }
      this.#purgeOrphanedChunks()

      return JSON.parse(parts.join(''))
    } catch {
      return null
    }
  }

  getAllChats({resetLoading = false} = {}) {
    try {
      const chatsListRaw = this.#localStorage.getItem(this.#keys.CHATS_LIST)
      if (!chatsListRaw) return []

      const chatsList = JSON.parse(chatsListRaw)
      const now = Date.now()
      const validChats = []
      const expiredChatIds = []

      // Filter out chats older than 7 days or whose conversation data no longer exists
      const staleChatIds = []
      chatsList.forEach(chat => {
        if (now - chat.savedAt > this.#historyTtlMs) {
          expiredChatIds.push(chat.id)
        } else if (!this.#localStorage.getItem(this.#keys.chatMeta(chat.id))) {
          staleChatIds.push(chat.id)
        } else {
          if (chat.loading && resetLoading) {
            chat.loading = false
            staleChatIds.push(chat.id)
          }

          validChats.push(chat)
        }
      })

      // Delete expired chats from storage
      expiredChatIds.forEach(chatId => this.#clearChat(chatId))

      // Update the chats list without expired or stale entries
      if (expiredChatIds.length > 0 || staleChatIds.length > 0) {
        this.#localStorage.setItem(this.#keys.CHATS_LIST, JSON.stringify(validChats))
      }

      // Sort by most recent first
      return validChats.sort((a, b) => b.savedAt - a.savedAt)
    } catch {
      return []
    }
  }

  deleteChatById(chatId) {
    try {
      this.#clearChat(chatId)
      this.#removeChatFromList(chatId)

      const indexRaw = this.#localStorage.getItem(this.#keys.INDEX)
      if (indexRaw) {
        const indexData = JSON.parse(indexRaw)
        if (indexData.activeId === chatId) this.#localStorage.removeItem(this.#keys.INDEX)
      }
    } catch {
      // ignore
    }
  }

  #clearChat(chatId) {
    try {
      const metaRaw = this.#localStorage.getItem(this.#keys.chatMeta(chatId))
      if (metaRaw) {
        const {totalChunks} = JSON.parse(metaRaw)
        for (let i = 0; i < totalChunks; i++) {
          this.#localStorage.removeItem(this.#keys.chunk(chatId, i))
        }
      }
      this.#localStorage.removeItem(this.#keys.chatMeta(chatId))
    } catch {
      // ignore
    }
  }
}
